import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import profileService from '../../services/profileService';

/**
 * First-login product tour state. It opens once on a new account's first sign-in (the server
 * says whether this person has seen it — accounts that predate the tour are already marked),
 * can be restarted from the profile menu, and records "seen" when finished or dismissed.
 * Platform operators never get it.
 */
const TourContext = createContext({ open: false, start: () => {}, finish: () => {} });

export const useTour = () => useContext(TourContext);

// Wait for the shell to settle (sidebar, first page) before covering it.
const AUTO_OPEN_DELAY_MS = 900;

export function TourProvider({ children }) {
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  // Asked once per session. A ref, not state: setting state would re-run the effect, and its
  // cleanup would cancel the pending open.
  const checked = useRef(false);
  const operator = user?.role === 'SUPER_ADMIN';

  useEffect(() => {
    if (!isAuthenticated || operator || checked.current) return undefined;
    let cancelled = false;
    let timer = null;
    profileService.getTourState()
      .then((s) => {
        if (cancelled) return;
        checked.current = true;
        if (s?.seen) return;
        timer = setTimeout(() => { if (!cancelled) setOpen(true); }, AUTO_OPEN_DELAY_MS);
      })
      .catch(() => { /* no tour beats a broken one */ });
    return () => { cancelled = true; clearTimeout(timer); };
  }, [isAuthenticated, operator]);

  // A full-page route (onboarding wizard, a test editor) has no sidebar to point at.
  const shellRoute = !location.pathname.startsWith('/onboarding');

  const start = useCallback(() => setOpen(true), []);
  const finish = useCallback(() => {
    setOpen(false);
    profileService.setTourSeen(true).catch(() => {});
  }, []);

  const value = useMemo(() => ({ open: open && shellRoute, start, finish }), [open, shellRoute, start, finish]);
  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}

export default TourContext;
